import type { CSSProperties } from 'react'
import type { Direction, Orientation } from './types'

/**
 * Chakra UI slider helper utilities.
 * Modified and converted to plain Javascript.
 *
 * @see Docs     https://chakra-ui.com/docs/form/slider
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/slider/src/slider-utils.ts
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#slider
 */

export function getIds(id: string | number) {
  return {
    root: `slider-root-${id}`,
    getThumb: (i: number) => `slider-thumb-${id}-${i}`,
    getInput: (i: number) => `slider-input-${id}-${i}`,
    track: `slider-track-${id}`,
    innerTrack: `slider-filled-track-${id}`,
    getMarker: (i: number) => `slider-marker-${id}-${i}`,
    output: `slider-output-${id}`
  }
}

type OrientOptions = {
  orientation: Orientation
  vertical: CSSProperties
  horizontal: CSSProperties
}

export function orient(options: OrientOptions) {
  return options.orientation === 'vertical'
    ? options.vertical
    : options.horizontal
}

type Size = { height: number; width: number }

const zeroRect: Size = { width: 0, height: 0 }

type GetStylesOptions = {
  orientation: Orientation
  thumbPercents: number[]
  thumbRects: Size[]
  isReversed?: boolean
}

export function getStyles(options: GetStylesOptions) {
  const { orientation, thumbPercents, thumbRects, isReversed } = options

  const getThumbStyle = (i: number): CSSProperties => ({
    position: 'absolute',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    touchAction: 'none',
    ...orient({
      orientation,
      vertical: {
        bottom: `calc(${thumbPercents[i]}% - ${thumbRects[i].height / 2}px)`
      },
      horizontal: {
        left: `calc(${thumbPercents[i]}% - ${thumbRects[i].width / 2}px)`
      }
    })
  })

  const size =
    orientation === 'vertical'
      ? thumbRects.reduce((a, b) => (a.height > b.height ? a : b), zeroRect)
      : thumbRects.reduce((a, b) => (a.width > b.width ? a : b), zeroRect)

  const rootStyle: CSSProperties = {
    position: 'relative',
    touchAction: 'none',
    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
    userSelect: 'none',
    outline: 0,
    ...orient({
      orientation,
      vertical: {
        paddingLeft: size.width / 2,
        paddingRight: size.width / 2
      },
      horizontal: {
        paddingTop: size.height / 2,
        paddingBottom: size.height / 2
      }
    })
  }

  const trackStyle: CSSProperties = {
    position: 'absolute',
    ...orient({
      orientation,
      vertical: {
        left: '50%',
        transform: 'translateX(-50%)',
        height: '100%'
      },
      horizontal: {
        top: '50%',
        transform: 'translateY(-50%)',
        width: '100%'
      }
    })
  }

  const isSingleThumb = thumbPercents.length === 1
  const fallback = [0, isReversed ? 100 - thumbPercents[0] : thumbPercents[0]]
  const range = isSingleThumb ? fallback : thumbPercents

  let start = range[0]
  if (!isSingleThumb && isReversed) {
    start = 100 - start
  }
  const percent = Math.abs(range[range.length - 1] - range[0])

  const innerTrackStyle: CSSProperties = {
    ...trackStyle,
    ...orient({
      orientation,
      vertical: isReversed
        ? { height: `${percent}%`, top: `${start}%` }
        : { height: `${percent}%`, bottom: `${start}%` },
      horizontal: isReversed
        ? { width: `${percent}%`, right: `${start}%` }
        : { width: `${percent}%`, left: `${start}%` }
    })
  }

  return { trackStyle, innerTrackStyle, rootStyle, getThumbStyle }
}

type GetIsReversedOptions = {
  isReversed?: boolean
  direction: Direction
  orientation?: Orientation
}

export function getIsReversed(options: GetIsReversedOptions) {
  if (options.direction === 'ltr' || options.orientation === 'vertical') {
    return options.isReversed
  }

  return !options.isReversed
}

export function getDefaultValue(min: number, max: number) {
  return max < min ? min : min + (max - min) / 2
}
